// Facebook-style NotificationBell component
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import API_URL from '../api/baseURL';

const NotificationBell = ({ notifications = [], onRefresh }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClick = async (notif) => {
    if (!token) {
      navigate('/signup');
      setOpen(false);
      return;
    }

    // Mark as read first
    if (!notif.read) {
      try {
        await axios.patch(
          `${API_URL}/notifications/${notif._id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (onRefresh) onRefresh();
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }

    // Navigate to linked page
    if (notif.link) {
      setOpen(false);
      navigate(notif.link);
    } else {
      // Fallback navigation based on type
      setOpen(false);
      navigate('/notifications');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'request_accepted': { icon: '✓', bg: 'bg-green-500' },
      'request_rejected': { icon: '✕', bg: 'bg-red-500' },
      'request_rescheduled': { icon: '📅', bg: 'bg-blue-500' },
      'quotation_received': { icon: '📄', bg: 'bg-purple-500' },
      'quotation_decision': { icon: '📄', bg: 'bg-purple-500' },
      'quotation_updated': { icon: '📄', bg: 'bg-purple-500' },
      'quotation_revised': { icon: '📄', bg: 'bg-orange-500' },
      'quotation_action': { icon: '📄', bg: 'bg-purple-500' },
      'quotation_accepted': { icon: '✓', bg: 'bg-green-500' },
      'invoice_created': { icon: '🧾', bg: 'bg-indigo-500' },
      'payment_confirmed': { icon: '💳', bg: 'bg-green-600' },
      'insurance_expiring': { icon: '🛡️', bg: 'bg-yellow-500' },
      'technical_expiring': { icon: '🔧', bg: 'bg-yellow-500' },
      // Supplier RFQ channel
      'rfq_received': { icon: '📥', bg: 'bg-amber-500' },
      'supplier_quote_received': { icon: '📄', bg: 'bg-purple-500' },
      'quote_countered': { icon: '⇄', bg: 'bg-orange-500' },
      'quote_accepted': { icon: '✓', bg: 'bg-green-500' },
      'quote_rejected': { icon: '✕', bg: 'bg-red-500' },
      // Payment receipts
      'payment_receipt': { icon: '🧾', bg: 'bg-green-600' },
      'payment_receipt_cancelled': { icon: '🧾', bg: 'bg-gray-500' },
    };
    return icons[type] || { icon: '🔔', bg: 'bg-gray-500' };
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-gray-700 transition-colors duration-200"
        aria-label="Notifications"
      >
        <svg
          className="w-6 h-6 text-gray-200"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel - Facebook Style */}
      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
            <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              See All
            </button>
          </div>

          {/* Notification List */}
          <div className="max-h-[420px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="text-5xl mb-3">🔔</div>
                <p className="text-gray-500">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((notif) => {
                const { icon, bg } = getNotificationIcon(notif.type);
                return (
                  <div
                    key={notif._id}
                    onClick={() => handleClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors duration-150 hover:bg-gray-50 ${!notif.read ? 'bg-blue-50' : ''
                      }`}
                  >
                    {/* Icon Circle */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full ${bg} flex items-center justify-center text-white text-lg shadow-sm`}>
                      {icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {notif.message}
                      </p>
                      <p className={`text-xs mt-1 ${!notif.read ? 'text-blue-500 font-medium' : 'text-gray-400'}`}>
                        {getTimeAgo(notif.createdAt)}
                      </p>
                    </div>

                    {/* Unread Indicator */}
                    {!notif.read && (
                      <div className="flex-shrink-0 w-3 h-3 rounded-full bg-blue-500 mt-1.5"></div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 10 && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => { setOpen(false); navigate('/notifications'); }}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium py-1"
              >
                View all {notifications.length} notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
